import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, FileTextIcon } from 'lucide-react';
import { useClient } from './ClientContext';
const RECAP_URL = 'https://tina.avy.me/recap.html';
export function Hero() {
  const {
    clientName
  } = useClient();
  const firstName = clientName.split(' ')[0] || 'Tina';
  return <section className="pt-24 md:pt-28 pb-0 px-5 md:px-10 border-b border-rule/60 relative overflow-hidden" data-id="element-91">
      <div className="max-w-6xl mx-auto relative" data-id="element-92">
        {/* Top meta */}
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="flex items-center justify-between mb-12 md:mb-16 text-[10px] uppercase tracking-[0.25em] text-ink-mute font-medium" data-id="element-93">
          <span className="flex items-center gap-2" data-id="element-94">
            <span className="w-1 h-1 rounded-full bg-red" data-id="element-95" />
            Proposal — N°01
          </span>
          <span className="hidden sm:flex items-center gap-2" data-id="element-96">
            <span className="w-1 h-1 rounded-full bg-red" data-id="element-97" />
            Prepared for {clientName}
          </span>
          <span className="flex items-center gap-2" data-id="element-98">
            <span className="w-1 h-1 rounded-full bg-red" data-id="element-99" />
            05.05.26
          </span>
        </motion.div>

        {/* Headline grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 mb-16 md:mb-24 items-end" data-id="element-100">
          <motion.div className="md:col-span-8" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-101">
            <p className="text-[11px] uppercase tracking-[0.3em] text-red font-bold mb-6" data-id="element-102">
              Brand Proposal — 2026
            </p>
            <h1 className="font-sans text-[52px] sm:text-7xl md:text-[112px] lg:text-[140px] leading-[0.88] tracking-[-0.045em] text-ink font-light" data-id="element-103">
              A brand
              <br data-id="element-104" />
              built with
              <br data-id="element-105" />
              <span className="font-bold italic text-ink relative inline-block" data-id="element-106">
                clarity
                <motion.span className="absolute -right-3 md:-right-5 top-2 md:top-6 w-3 h-3 md:w-5 md:h-5 rounded-full bg-red" initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 1,
                type: 'spring',
                stiffness: 200
              }} data-id="element-107" />
              </span>
            </h1>
          </motion.div>

          <motion.div className="md:col-span-4 md:pb-4" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.5
        }} data-id="element-108">
            <p className="text-[10px] uppercase tracking-[0.25em] text-red mb-3 font-bold" data-id="element-109">
              Prepared by
            </p>
            <p className="text-2xl md:text-3xl text-red mb-1 font-light tracking-tight" data-id="element-110">
              Avy Pruthi
            </p>
            <p className="text-xs text-red/80 mb-5 font-medium" data-id="element-111">
              Founder &amp; Director, AVY Consultancy
            </p>
            <svg width="110" height="36" viewBox="0 0 120 50" fill="none" data-id="element-112">
              <motion.path d="M5 38C8 28 12 15 18 12C24 9 22 30 26 28C30 26 32 10 38 14C44 18 40 35 46 32C52 29 56 8 62 12C68 16 64 34 70 30C76 26 82 10 86 16C90 22 88 38 94 34C100 30 104 18 108 22C112 26 115 35 115 35" stroke="#E84B1A" strokeWidth="1.8" strokeLinecap="round" fill="none" initial={{
              pathLength: 0
            }} animate={{
              pathLength: 1
            }} transition={{
              duration: 1.6,
              delay: 0.9
            }} data-id="element-113" />
            </svg>
          </motion.div>
        </div>

        {/* Intro + recap CTA + details */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 mb-16 md:mb-20" data-id="element-114">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.4
        }} className="md:col-span-7" data-id="element-115">
            <p className="text-base md:text-xl text-ink-soft leading-relaxed font-light max-w-xl mb-8" data-id="element-116">
              Hi {firstName}, thank you again for your time. Following our
              clarity call, this proposal outlines a focused approach to shaping
              your brand with more clarity, confidence and commercial direction
              — and recaps the key opportunities we identified together.
            </p>

            <motion.a href={RECAP_URL} target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="group inline-flex items-center gap-4 bg-ink text-ivory pl-5 pr-6 py-3.5 rounded-full hover:bg-red transition-colors duration-500" data-id="element-117">
              <span className="w-8 h-8 rounded-full bg-ivory/10 flex items-center justify-center" data-id="element-118">
                <FileTextIcon className="w-3.5 h-3.5" data-id="element-119" />
              </span>
              <span className="text-[11px] uppercase tracking-[0.2em] font-bold" data-id="element-120">
                View Clarity Call Recap
              </span>
              <ArrowUpRightIcon className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-121" />
            </motion.a>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="md:col-span-5 grid grid-cols-2 gap-y-6 gap-x-4 content-end" data-id="element-122">
            {[{
            label: 'Project',
            value: 'MVB Brand',
            sub: 'Identity Design'
          }, {
            label: 'Client',
            value: 'Tina Uberoi'
          }, {
            label: 'Date',
            value: '05.05.26'
          }, {
            label: 'Valid until',
            value: '08.05.26'
          }].map(item => <div key={item.label} className="border-t border-ink pt-3" data-id="element-123">
                <p className="text-[10px] uppercase tracking-[0.25em] text-ink-mute mb-1.5 font-medium" data-id="element-124">
                  {item.label}
                </p>
                <p className="text-sm md:text-base text-ink font-bold leading-tight" data-id="element-125">
                  {item.value}
                </p>
                {item.sub && <p className="text-xs text-ink-mute mt-0.5" data-id="element-126">{item.sub}</p>}
              </div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}